import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
    LayoutDashboard, Wallet, Network, Users, UsersRound, Gift,
    LogOut, Shield, CalendarClock, UserPlus, FileCheck, Menu, X, Settings, TrendingUp, BarChart3,
} from "lucide-react";

const memberNav = [
    { to: "/app/dashboard", label: "Dashboard",  icon: LayoutDashboard },
    { to: "/app/add-member",label: "Add Member",  icon: UserPlus        },
    { to: "/app/wallet",    label: "Wallet",      icon: Wallet          },
    { to: "/app/tree",      label: "My Tree",     icon: Network         },
    { to: "/app/team",      label: "My Team",     icon: UsersRound      },
    { to: "/app/pairing",   label: "Pairing",     icon: Gift            },
    { to: "/app/kyc",       label: "KYC Verify",  icon: FileCheck       },
];

const adminNav = [
    { to: "/admin",                  label: "Admin Home",       icon: Shield        },
    { to: "/admin/users",            label: "Users",            icon: Users         },
    { to: "/admin/business-income",  label: "Business Income",  icon: TrendingUp    },
    { to: "/admin/member-earnings",  label: "Member Earnings",  icon: UsersRound    },
    { to: "/admin/pairing",          label: "Pairing Runs",     icon: CalendarClock },
    { to: "/admin/kyc",              label: "KYC Review",       icon: FileCheck     },
    { to: "/admin/reports",          label: "Income Reports",   icon: BarChart3     },
    { to: "/admin/network-tree",      label: "Network Tree",     icon: Network       },
    { to: "/admin/settings",         label: "Platform Settings",icon: Settings      },
];

/* ── initials avatar ──────────────────────────────────────────────── */
function UserAvatar({ name = "" }) {
    const letters = name.split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase() || "U";
    return (
        <div className="w-11 h-11 rounded-full bg-white/15 border-2 border-white/25 grid place-items-center font-black text-white text-sm flex-shrink-0">
            {letters}
        </div>
    );
}

/* ── sidebar body shared between desktop and mobile drawer ─────────── */
function SidebarBody({ items, admin, user, kycBadge, onClose, onLogout }) {
    return (
        <div className="flex flex-col h-full">
            {/* User profile */}
            {user && (
                <div className="px-4 py-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <UserAvatar name={user.full_name} />
                        <div className="min-w-0">
                            <div className="text-sm font-bold text-white truncate leading-tight">{user.full_name}</div>
                            <div className="text-[11px] font-mono font-bold text-blue-200 tracking-widest mt-0.5">{user.referral_code}</div>
                            <div className="text-[10px] text-white/40 truncate mt-0.5">{user.email}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">
                {items.map(({ to, label, icon: Icon }) => (
                    <NavLink
                        key={to}
                        to={to}
                        end
                        onClick={onClose}
                        data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                isActive
                                    ? "bg-white/18 text-white"
                                    : "text-white/60 hover:bg-white/10 hover:text-white"
                            }`
                        }
                    >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="flex-1">{label}</span>
                        {label === "KYC Verify" && kycBadge && (
                            <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                        )}
                    </NavLink>
                ))}

                {user?.role === "admin" && !admin && (
                    <NavLink
                        to="/admin"
                        onClick={onClose}
                        data-testid="nav-admin"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                isActive ? "bg-white/18 text-white" : "text-white/60 hover:bg-white/10 hover:text-white"
                            }`
                        }
                    >
                        <Shield className="w-4 h-4 flex-shrink-0" /> Admin
                    </NavLink>
                )}
                {admin && (
                    <NavLink
                        to="/app/dashboard"
                        onClick={onClose}
                        data-testid="nav-member-mode"
                        className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:bg-white/10 hover:text-white transition-all"
                    >
                        <LayoutDashboard className="w-4 h-4 flex-shrink-0" /> Member Mode
                    </NavLink>
                )}
            </nav>

            {/* Bottom: Settings + Logout */}
            <div className="px-3 pb-4 pt-2 border-t border-white/10 space-y-0.5">
                <button className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:bg-white/10 hover:text-white transition-all">
                    <Settings className="w-4 h-4 flex-shrink-0" /> Settings
                </button>
                <button
                    onClick={onLogout}
                    data-testid="logout-btn"
                    className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:bg-red-500/20 hover:text-red-300 transition-all"
                >
                    <LogOut className="w-4 h-4 flex-shrink-0" /> Log out
                </button>
            </div>
        </div>
    );
}

/* ── main shell ───────────────────────────────────────────────────── */
export default function AppShell({ children, admin = false }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const items = admin ? adminNav : memberNav;

    const handleLogout = () => { logout(); nav("/login"); };
    const kycBadge = !admin && user && user.kyc_status !== "approved";

    return (
        <div className="h-screen flex bg-[#EEF4FB] overflow-hidden">

            {/* ── Desktop sidebar ── */}
            <aside className="hidden md:flex w-56 flex-col bg-[#1B2B6B] flex-shrink-0">
                {/* Brand */}
                <div className="px-5 pt-6 pb-5 border-b border-white/10 flex-shrink-0">
                    <Link to="/" className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-xl bg-white/15 border border-white/20 grid place-items-center text-white font-black text-sm flex-shrink-0">Z</span>
                        <span className="font-display font-black text-xl tracking-tight text-white">Zenwyxon</span>
                    </Link>
                </div>
                <SidebarBody
                    items={items} admin={admin} user={user}
                    kycBadge={kycBadge} onClose={() => {}} onLogout={handleLogout}
                />
            </aside>

            {/* ── Mobile overlay ── */}
            {drawerOpen && (
                <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={() => setDrawerOpen(false)} />
            )}

            {/* ── Mobile drawer ── */}
            <div className={`fixed inset-y-0 left-0 z-50 w-60 bg-[#1B2B6B] flex flex-col shadow-2xl transform transition-transform duration-300 md:hidden ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}>
                <div className="px-5 pt-5 pb-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
                    <Link to="/" onClick={() => setDrawerOpen(false)} className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-xl bg-white/15 border border-white/20 grid place-items-center text-white font-black text-sm">Z</span>
                        <span className="font-display font-black text-xl text-white tracking-tight">Zenwyxon</span>
                    </Link>
                    <button onClick={() => setDrawerOpen(false)} className="p-1.5 rounded-lg text-white/60 hover:bg-white/10">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                    <SidebarBody
                        items={items} admin={admin} user={user}
                        kycBadge={kycBadge} onClose={() => setDrawerOpen(false)} onLogout={handleLogout}
                    />
                </div>
            </div>

            {/* ── Main content ── */}
            <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                {/* Mobile top bar */}
                <header className="md:hidden sticky top-0 z-30 bg-[#1B2B6B] px-4 h-14 flex items-center justify-between shadow-md flex-shrink-0">
                    <button
                        onClick={() => setDrawerOpen(true)}
                        className="p-2 rounded-lg text-white/70 hover:bg-white/10 transition-colors"
                        aria-label="Open menu"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <Link to="/" className="flex items-center gap-2 font-display font-black text-lg text-white tracking-tight">
                        <span className="w-7 h-7 rounded-lg bg-white/15 grid place-items-center text-white font-black text-sm">Z</span>
                        Zenwyxon
                    </Link>
                    <div className="w-9" />
                </header>

                <main className="flex-1 overflow-auto p-4 md:p-8">{children}</main>
            </div>
        </div>
    );
}
